/**
 * new types to model the Vimeo response
 */
export interface Pictures {
  sizes: { link: string }[]
}
export interface ProgressiveFormat {
  type: string
  codec: string
  width: number
  height: number
  link_expiration_time: string
  link: string
  created_time: string
  fps: number
  size: number
  md5: string | null
  rendition: VimeoRendition
}
export interface Hls {
  link_expiration_time: string
  link: string
}
export interface Dash {
  link_expiration_time: string
  link: string
}
export interface Play {
  progressive: ProgressiveFormat[]
  hls: Hls
  dash: Dash
  status: string
}

/**
 * Shape of the data returned from the Vimeo fetch endpoint
 */
export interface VimeoVideo {
  uri: string
  pictures: Pictures
  play: Play
}

export type VimeoRendition = '1080p' | '240p' | '2k' | '360p' | '480p' | '4k' | '540p' | '5k' | '6k' | '720p' | '7k' | '8k' | 'adaptive' | 'source'

/**
 * Fetches Vimeo video information by ID via Next.js API route
 * @param vimeoId - The Vimeo video identifier
 */
export const fetchVimeoVideo = async (vimeoId: string): Promise<VimeoVideo> => {
  // Call internal Next.js API to keep API key private
  const response = await fetch(`/api/videos/${vimeoId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch Vimeo video ${vimeoId}: ${response.status}`)
  }
  const json: VimeoVideo = await response.json()
  if (!json || !json.uri) {
    throw new Error(`No data found for Vimeo video ${vimeoId}`)
  }
  return json
}

/**
 * Gets the video URL for a specific rendition type from the Vimeo response
 * @param rendition - The desired video rendition (e.g. a specific progressive format like 720p,1080p or 480p)
 * @param vimeoResponse - The Vimeo video response object
 * @return The video URL for the specified rendition, or null if not found
 * */
export const getVimeoVideoByRendition = (rendition: VimeoRendition, vimeoResponse: VimeoVideo): string | null => {
  const { play } = vimeoResponse
  if (!play) return null
  return play.progressive.filter(format => format.rendition === rendition)[0]?.link || null
}

/**
 * Gets the picture URL for a specific resolution from the Vimeo response.
 *
 * @param resolution - Desired resolution as "widthxheight" (e.g. "1920x1080").
 * @param vimeoResponse - The Vimeo video response object containing picture sizes.
 * @returns The URL of the picture matching the resolution, or null if none found.
 */
export const getVimeoPictureByResolution = (resolution: string, vimeoResponse: VimeoVideo): string | null => {
  const { pictures } = vimeoResponse
  if (!pictures?.sizes) return null

  // look for the exact "_{width}x{height}" segment
  const picture = pictures.sizes.find(p => p.link.includes(`_${resolution}`))
  return picture?.link || null
}
